import { useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, Sparkles, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["rent", "Rent / Mortgage"], ["food", "Food"], ["utilities", "Utilities"],
  ["transportation", "Transportation"], ["insurance", "Insurance"], ["debt", "Debt payments"],
  ["childcare", "Childcare"], ["healthcare", "Healthcare"], ["other", "Other essentials"],
];
const CURRENCIES = ["USD", "GBP", "EUR", "CAD", "NGN", "AUD", "ZAR", "INR", "JPY", "KES"];

export default function EmergencyFund() {
  const [vals, setVals] = useState({});
  const [currency, setCurrency] = useState("USD");
  const [savings, setSavings] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const set = (k, v) => setVals((p) => ({ ...p, [k]: v }));
  const fmt = (n) => `${currency} ${Number(n || 0).toLocaleString()}`;

  const calc = async () => {
    setLoading(true); setRes(null);
    try {
      const payload = { currency, current_savings: parseFloat(savings) || 0 };
      FIELDS.forEach(([k]) => (payload[k] = parseFloat(vals[k]) || 0));
      const { data } = await api.post("/emergency-fund", payload);
      setRes(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Calculation failed."); }
    finally { setLoading(false); }
  };

  return (
    <main className="mx-auto max-w-5xl px-6 py-10 md:px-8">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500"><ShieldCheck size={14} strokeWidth={1.5} /> Emergency Fund</p>
      <h1 className="mt-2 font-head text-3xl font-medium tracking-tight text-stone-900 sm:text-4xl">Emergency Fund Calculator</h1>
      <p className="mt-2 max-w-2xl text-stone-600">Enter your monthly essential expenses. We'll calculate 3, 6 and 12-month targets and tell you whether to build savings before investing.</p>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-stone-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm font-semibold text-stone-900">Monthly essential expenses</p>
            <select data-testid="currency-select" value={currency} onChange={(e) => setCurrency(e.target.value)}
              className="rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20">
              {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {FIELDS.map(([k, label]) => (
              <div key={k}>
                <label className="text-xs font-medium text-stone-500">{label}</label>
                <input data-testid={`expense-${k}`} type="number" min="0" value={vals[k] || ""} onChange={(e) => set(k, e.target.value)}
                  placeholder="0"
                  className="mt-1 h-11 w-full rounded-lg border border-stone-200 bg-white px-3 text-sm tabular focus:outline-none focus:ring-2 focus:ring-navy/20" />
              </div>
            ))}
          </div>
          <div className="mt-4 border-t border-stone-100 pt-4">
            <label className="text-xs font-medium text-stone-500">Current emergency savings</label>
            <input data-testid="current-savings" type="number" min="0" value={savings} onChange={(e) => setSavings(e.target.value)}
              placeholder="0"
              className="mt-1 h-11 w-full rounded-lg border border-stone-200 bg-white px-3 text-sm tabular focus:outline-none focus:ring-2 focus:ring-navy/20" />
          </div>
          <button data-testid="calculate-btn" disabled={loading} onClick={calc}
            className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-navy text-sm font-medium text-white transition-colors hover:bg-navyd disabled:opacity-60">
            <Sparkles size={16} strokeWidth={1.5} /> {loading ? "Calculating…" : "Calculate my target"}
          </button>
        </div>

        <div>
          {!res && !loading && (
            <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-stone-300 bg-white/50 p-10 text-center text-sm text-stone-400">
              Your targets and recommendation will appear here.
            </div>
          )}
          {loading && (
            <div className="flex h-full items-center justify-center rounded-xl border border-stone-200 bg-white p-10">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-stone-300 border-t-navy" />
            </div>
          )}
          {res && !loading && (
            <div className="space-y-4 animate-in-up" data-testid="ef-result">
              <div className="rounded-xl border border-stone-200 bg-white p-5">
                <p className="text-sm text-stone-500">Monthly essentials</p>
                <p className="tabular text-2xl font-semibold text-stone-900">{fmt(res.monthly_essentials)}</p>
                <p className="mt-1 text-sm text-stone-500">You currently have <span className="font-semibold text-stone-800">{res.months_covered} months</span> covered.</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[["3-month", res.targets.three_month], ["6-month", res.targets.six_month], ["12-month", res.targets.twelve_month]].map(([l, v], i) => (
                  <div key={i} data-testid={`target-${i}`} className="rounded-xl border border-stone-200 bg-white p-4 text-center">
                    <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">{l}</p>
                    <p className="tabular mt-1 text-base font-semibold text-navy">{fmt(v)}</p>
                  </div>
                ))}
              </div>
              <div className={`rounded-xl border p-5 ${res.should_build_first ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="ef-recommendation">
                <div className="flex items-center gap-2">
                  {res.should_build_first ? <AlertCircle size={18} className="text-amber-700" /> : <CheckCircle2 size={18} className="text-emerald-700" />}
                  <p className={`text-sm font-semibold ${res.should_build_first ? "text-amber-800" : "text-navy"}`}>
                    {res.should_build_first ? "Build your emergency fund first" : "You're on solid ground"}
                  </p>
                </div>
                <p className="mt-2 text-sm text-stone-700">{res.recommendation}</p>
                {res.action_steps?.length > 0 && (
                  <ul className="mt-3 list-disc space-y-1 pl-4 text-sm text-stone-700">
                    {res.action_steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                )}
              </div>
              <p className="text-center text-xs text-stone-400">{res.disclaimer}</p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
